import React, { useState, useEffect } from "react";
import { customersAPI, countiesAPI } from "../services/api";
import SearchableSelect from "./SearchableSelect";

const CustomerModal = ({ customer, onClose }) => {
  const [formData, setFormData] = useState({
    customer_name: "",
    email:         "",
    mobile_number: "",
    county:        "",
  });
  const [counties, setCounties] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  useEffect(() => {
    fetchCounties();
    if (customer) {
      setFormData({
        customer_name: customer.customer_name || "",
        email:         customer.email         || "",
        mobile_number: customer.mobile_number || customer.contact_number || "",
        county:        customer.county        || "",
      });
    }
  }, [customer]);

  const fetchCounties = async () => {
    try {
      const res = await countiesAPI.getAll();
      setCounties(res.data);
    } catch (err) {
      console.error("Failed to load counties:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.customer_name.trim()) {
      setError("Customer name is required");
      setLoading(false);
      return;
    }
    if (!formData.mobile_number.trim()) {
      setError("Mobile number is required");
      setLoading(false);
      return;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }
    const phoneRegex = /^(\+254|254|0)?[1-9]\d{8}$/;
    if (!phoneRegex.test(formData.mobile_number.replace(/\s/g, ""))) {
      setError("Please enter a valid Kenyan mobile number (e.g., 0712345678 or +254712345678)");
      setLoading(false);
      return;
    }

    try {
      const dataToSend = {
        customer_name: formData.customer_name.trim(),
        mobile_number: formData.mobile_number.trim(),
      };
      if (formData.email?.trim())  dataToSend.email  = formData.email.trim();
      if (formData.county?.trim()) dataToSend.county = formData.county.trim();

      if (customer) {
        await customersAPI.update(customer.id, dataToSend);
      } else {
        await customersAPI.create(dataToSend);
      }
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error   ||
        err.message                 ||
        "Failed to save customer. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const countyOptions = counties.map(c => ({ value: c.name, label: c.name }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h3 className="text-lg font-semibold text-gray-900">
              {customer ? "Edit Customer" : "Add New Customer"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="customer_name" className="form-label">Customer Name *</label>
              <input
                type="text"
                id="customer_name"
                name="customer_name"
                required
                className="form-input"
                placeholder="Enter customer name"
                value={formData.customer_name}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="mobile_number" className="form-label">Mobile Number *</label>
              <input
                type="tel"
                id="mobile_number"
                name="mobile_number"
                required
                className="form-input"
                placeholder="0712345678 or +254712345678"
                value={formData.mobile_number}
                onChange={handleChange}
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">Format: +254XXXXXXXXX or 07XXXXXXXX</p>
            </div>

            <div>
              <label htmlFor="email" className="form-label">Email Address (Optional)</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label className="form-label">County (Optional)</label>
              <SearchableSelect
                value={formData.county}
                onChange={v => setFormData(prev => ({ ...prev, county: v }))}
                options={countyOptions}
                placeholder="Please select county"
                disabled={loading}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 rounded-b-xl flex flex-col sm:flex-row-reverse gap-3">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full sm:w-auto"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="loading-spinner" /> Saving...
                </span>
              ) : customer ? "Update Customer" : "Add Customer"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn btn-secondary w-full sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerModal;
